/**
 * Telling the administrator a check has arrived when they are looking at
 * something else: the tab title counts them, and (when alerts are on) a short
 * tone plays and a desktop notification shows. A check waits a couple of
 * minutes at most, so noticing it is most of the job.
 */
import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { reasonOf, subjectOf } from '@/features/live/live'
import type { LiveItem } from '@/types/api'

interface AlertState {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
}

export const useAlertStore = create<AlertState>()(
  persist((set) => ({ enabled: false, setEnabled: (enabled) => set({ enabled }) }), { name: 'job-seeker-admin-alerts' }),
)

let audio: AudioContext | null = null

/** Browsers only let sound start from a click: call this from one. */
export function primeSound(): void {
  try {
    audio ??= new AudioContext()
    void audio.resume()
  } catch {
    audio = null
  }
}

function chime(): void {
  if (!audio || audio.state !== 'running') return
  const at = audio.currentTime
  for (const [offset, frequency] of [[0, 880], [0.16, 1175]] as const) {
    const oscillator = audio.createOscillator()
    const gain = audio.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    gain.gain.setValueAtTime(0.0001, at + offset)
    gain.gain.exponentialRampToValueAtTime(0.18, at + offset + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + offset + 0.22)
    oscillator.connect(gain).connect(audio.destination)
    oscillator.start(at + offset)
    oscillator.stop(at + offset + 0.24)
  }
}

/** Turn alerts on (asking the browser's permission) or off. Returns what was set. */
export async function setAlerts(on: boolean): Promise<boolean> {
  if (on) {
    primeSound()
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission().catch(() => undefined)
    }
    chime()
  }
  useAlertStore.getState().setEnabled(on)
  return on
}

const BASE_TITLE = 'JobSlide Admin'

/** Mounted once, in the shell: counts in the tab title, and alerts for each new arrival. */
export function useLiveAlerts(items: LiveItem[], onOpen: (handoffId: string) => void): void {
  const enabled = useAlertStore((state) => state.enabled)
  const seen = useRef<Set<string> | null>(null)

  useEffect(() => {
    document.title = items.length ? `(${items.length}) ${BASE_TITLE}` : BASE_TITLE
    return () => {
      document.title = BASE_TITLE
    }
  }, [items.length])

  // Alerts were left on from an earlier visit: sound still needs one click in this one.
  useEffect(() => {
    if (!enabled) return
    const prime = () => primeSound()
    window.addEventListener('pointerdown', prime, { once: true })
    return () => window.removeEventListener('pointerdown', prime)
  }, [enabled])

  const ids = items.map((item) => item.handoffId).join('|')
  useEffect(() => {
    const current = new Set(items.map((item) => item.handoffId))
    const before = seen.current
    seen.current = current
    // The first answer after opening the portal is not news.
    if (before === null || !enabled) return
    const arrived = items.filter((item) => !before.has(item.handoffId))
    if (!arrived.length) return
    chime()
    if (!('Notification' in window) || Notification.permission !== 'granted' || document.visibilityState === 'visible') return
    const first = arrived[0]!
    const subject = subjectOf(first)
    try {
      const notification = new Notification(`${reasonOf(first.reason).label} waiting${arrived.length > 1 ? ` (+${arrived.length - 1} more)` : ''}`, {
        body: [first.user?.fullName || first.user?.email, subject.title].filter(Boolean).join(' · '),
        tag: 'tsenta-admin-live',
      })
      notification.onclick = () => {
        window.focus()
        onOpen(first.handoffId)
        notification.close()
      }
    } catch {
      /* some browsers only allow notifications from a service worker */
    }
    // `ids` stands for `items`: the effect should run when the set changes, not every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, enabled])
}
